import { useState } from "react";
import { Header } from "../components/Header";
import { IdeaForm } from "../components/IdeaForm";
import { getDisplayName } from "../lib/displayName";
import type { User } from "../auth/useUser";
import {
  DEMO_TREE,
  DEMO_OPEN_QUESTIONS,
  DEMO_PENDING_DECISIONS,
  DEMO_CONFIRMED_DECISIONS,
  DEMO_RUN_COUNT,
} from "../data/demoProduct";
import type { components } from "../api/types";

type TreeMeta = components["schemas"]["TreeMeta"];

// Three-step welcome flow for first-run founders.
//
//   1. Welcome — sets the mental model (idea -> analysis -> Q&A -> spec)
//   2. Demo    — read-only preview of an analyzed product so the founder
//                sees the destination before paying for the journey
//   3. Guided  — the real IdeaForm, with first-run framing copy
//
// Gating (whether to show this component at all) lives in
// useOnboardingState. This component owns step state + the
// localStorage-flag-on-completion side effect; it doesn't fetch anything
// of its own.

type Step = 1 | 2 | 3;

const EXAMPLES = [
  "AI-driven CRM for solo consultants",
  "Voice-first journaling app for runners",
  "Equipment-rental marketplace for film crews in EU",
];

export function Onboarding({
  user,
  onDismiss,
}: {
  user: User;
  // Called for both "skip" and "completed". The parent flips the
  // localStorage flag in either case (so the founder doesn't see the
  // overlay again on next refresh).
  onDismiss: () => void;
}) {
  const [step, setStep] = useState<Step>(1);

  const handleSkip = () => onDismiss();

  return (
    <div className="min-h-screen flex flex-col">
      <Header user={user} />

      <StepIndicator step={step} />

      <main className="flex-1 flex items-start justify-center px-6 pt-12 pb-32">
        {step === 1 && <WelcomeStep onNext={() => setStep(2)} onSkip={handleSkip} />}
        {step === 2 && (
          <DemoStep
            onNext={() => setStep(3)}
            onBack={() => setStep(1)}
            onSkip={handleSkip}
          />
        )}
        {step === 3 && (
          <GuidedStep
            onSkip={handleSkip}
            // IdeaForm fires onDismiss right before submit so the flag is
            // already set by the time the navigation to /products/:id
            // completes — refreshing the product page won't pop the
            // overlay back open.
            onSubmitStart={onDismiss}
          />
        )}
      </main>
    </div>
  );
}

// ---- Step indicator -------------------------------------------------

function StepIndicator({ step }: { step: Step }) {
  return (
    <div className="flex justify-center pt-8">
      <div className="flex items-center gap-2 font-mono text-xs text-ink-500">
        {[1, 2, 3].map((s) => {
          const active = s === step;
          const done = s < step;
          return (
            <span
              key={s}
              className={
                active
                  ? "text-accent"
                  : done
                    ? "text-ink-300"
                    : "text-ink-600"
              }
              aria-label={`Step ${s}${active ? " (current)" : ""}`}
            >
              {active ? "*" : "·"}
            </span>
          );
        })}
        <span className="ml-3 text-ink-500 tracking-widest uppercase">
          {step} / 3
        </span>
      </div>
    </div>
  );
}

// ---- Step 1: Welcome ------------------------------------------------

function WelcomeStep({
  onNext,
  onSkip,
}: {
  onNext: () => void;
  onSkip: () => void;
}) {
  return (
    <div className="relative w-full max-w-xl animate-slide-up">
      <div
        className="pointer-events-none absolute -top-32 left-1/2 h-[400px] w-[600px] -translate-x-1/2 rounded-full opacity-15 blur-3xl"
        style={{
          background: "radial-gradient(circle, rgba(124,122,255,0.6), transparent 70%)",
        }}
      />
      <div className="relative text-center">
        <div className="flex items-center justify-center gap-2 mb-6">
          <div className="h-1.5 w-1.5 rounded-full bg-accent" />
          <span className="font-mono text-xs text-ink-300 tracking-widest uppercase">
            Eminity
          </span>
        </div>

        <h1 className="text-display font-medium text-ink-50 tracking-tightest leading-[1.05]">
          Welcome to Eminity
        </h1>

        <p className="mt-6 text-base text-ink-300 leading-relaxed max-w-lg mx-auto">
          One sentence in, a build-ready spec out. Eminity runs your idea
          through a loop:
        </p>

        <ol className="mt-8 grid gap-3 text-left max-w-md mx-auto">
          {LOOP_STEPS.map((item, i) => (
            <li
              key={item.label}
              className="flex items-start gap-4 border border-ink-800 bg-ink-900/30 rounded-md px-4 py-3"
            >
              <span className="font-mono text-[10px] text-ink-500 mt-0.5 w-4 shrink-0">
                {String(i + 1).padStart(2, "0")}
              </span>
              <div>
                <p className="text-[13px] font-medium text-ink-100">
                  {item.label}
                </p>
                <p className="mt-0.5 text-[12px] text-ink-400 leading-relaxed">
                  {item.body}
                </p>
              </div>
            </li>
          ))}
        </ol>

        <div className="mt-10 flex items-center justify-center gap-6">
          <button
            type="button"
            onClick={onNext}
            className="group inline-flex items-center gap-2 rounded-md bg-accent px-4 py-2.5 text-sm font-medium text-ink-950 hover:bg-accent-glow transition-colors"
          >
            Take a tour
            <span className="opacity-60 group-hover:opacity-100 transition-opacity">→</span>
          </button>
          <button
            type="button"
            onClick={onSkip}
            className="text-xs font-mono text-ink-400 hover:text-ink-200 transition-colors"
          >
            skip — go to dashboard
          </button>
        </div>
      </div>
    </div>
  );
}

const LOOP_STEPS = [
  {
    label: "You describe an idea",
    body: "One or two sentences. The moat, who it's for, what it replaces.",
  },
  {
    label: "Eminity analyzes",
    body: "About 9 minutes of LLM work. Builds the spec scaffold and surfaces the gaps.",
  },
  {
    label: "You answer questions, confirm decisions",
    body: "We ask what only you can answer. You confirm the decisions we proposed.",
  },
  {
    label: "Re-run, refine, export",
    body: "Each pass tightens the spec. When you're satisfied, export it as Markdown.",
  },
];

// ---- Step 2: Demo ---------------------------------------------------

function DemoStep({
  onNext,
  onBack,
  onSkip,
}: {
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
}) {
  const meta = DEMO_TREE.meta;
  return (
    <div className="relative w-full max-w-4xl animate-slide-up">
      <div className="text-center mb-10">
        <p className="font-mono text-[10px] uppercase tracking-widest text-ink-400 mb-3">
          Sample · read-only
        </p>
        <h2 className="text-3xl font-medium text-ink-50 tracking-tightest">
          This is what an analyzed product looks like
        </h2>
        <p className="mt-3 text-sm text-ink-300 max-w-xl mx-auto leading-relaxed">
          A snapshot from a hypothetical Eminity user three runs in. No
          credits charged — this is just a preview.
        </p>
      </div>

      {/* Stripped-down product page mirroring the real layout: idea
          headline, queue cards, summary sections, side rail with rings.
          We keep the hover/focus styling muted because nothing here is
          actually clickable. */}
      <div className="border border-ink-800 bg-ink-900/30 rounded-lg p-8 grid grid-cols-[1fr_220px] gap-10">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-widest text-ink-400 mb-3">
            saas · last analyzed today · {DEMO_RUN_COUNT} runs
          </p>
          <h3 className="text-2xl font-medium text-ink-50 tracking-tightest leading-tight">
            {getDisplayName(meta)}
          </h3>

          <div className="mt-8 grid grid-cols-2 gap-3">
            <DemoTodoCard
              n={DEMO_OPEN_QUESTIONS}
              label="Open questions"
              accent="accent"
            />
            <DemoTodoCard
              n={DEMO_PENDING_DECISIONS}
              label="Pending decisions"
              accent="amber"
            />
          </div>

          <div className="mt-10 border-t border-ink-800 pt-6">
            <p className="font-mono text-[10px] uppercase tracking-widest text-ink-400 mb-5">
              Product summary
            </p>
            <div className="space-y-6">
              <DemoSection label="Moat" body={meta.moat} />
              <DemoSection label="Value mechanism" body={meta.value_mechanism} />
              <DemoSection label="Core experience" body={meta.core_experience} />
              <DemoSection label="Differentiation" body={meta.differentiation} />
              <DemoSection label="Abandonment triggers" body={meta.abandonment_triggers} />
            </div>
          </div>

          <div className="mt-8 border-t border-ink-800 pt-5 grid grid-cols-3 gap-2 text-center">
            <DemoStat n={DEMO_OPEN_QUESTIONS + DEMO_PENDING_DECISIONS} label="in queue" tone="accent" />
            <DemoStat n={DEMO_CONFIRMED_DECISIONS} label="decisions made" tone="emerald" />
            <DemoStat n={DEMO_RUN_COUNT} label="analysis runs" tone="ink" />
          </div>
        </div>

        <aside className="space-y-6">
          <DemoPanel label="Status">
            <DemoRingsList meta={meta} />
          </DemoPanel>
          <DemoPanel label="Visualization">
            <div className="border border-ink-800 bg-ink-900/40 rounded-md aspect-square flex items-center justify-center">
              <DemoRingsThumbnail />
            </div>
          </DemoPanel>
        </aside>
      </div>

      <div className="mt-10 flex items-center justify-between">
        <button
          type="button"
          onClick={onBack}
          className="text-xs font-mono text-ink-400 hover:text-ink-200 transition-colors"
        >
          ← back
        </button>
        <div className="flex items-center gap-6">
          <button
            type="button"
            onClick={onSkip}
            className="text-xs font-mono text-ink-400 hover:text-ink-200 transition-colors"
          >
            skip onboarding
          </button>
          <button
            type="button"
            onClick={onNext}
            className="group inline-flex items-center gap-2 rounded-md bg-accent px-4 py-2.5 text-sm font-medium text-ink-950 hover:bg-accent-glow transition-colors"
          >
            Got it, my turn
            <span className="opacity-60 group-hover:opacity-100 transition-opacity">→</span>
          </button>
        </div>
      </div>
    </div>
  );
}

function DemoTodoCard({
  n,
  label,
  accent,
}: {
  n: number;
  label: string;
  accent: "accent" | "amber";
}) {
  const numColor = n === 0 ? "text-ink-500" : accent === "accent" ? "text-accent" : "text-warn";
  return (
    <div className="border border-ink-800 bg-ink-900/40 rounded-md px-5 py-4">
      <div className="flex items-baseline justify-between">
        <span className={`text-3xl font-medium font-mono tracking-tight ${numColor}`}>
          {n}
        </span>
        <span className="text-xs text-ink-600">→</span>
      </div>
      <p className="mt-1 text-xs text-ink-300">{label}</p>
    </div>
  );
}

function DemoSection({ label, body }: { label: string; body?: string | null }) {
  if (!body) return null;
  return (
    <section>
      <h4 className="text-[12px] font-medium text-ink-100 mb-1.5 tracking-tight">
        {label}
      </h4>
      <p className="text-[13px] text-ink-300 leading-[1.7]">{body}</p>
    </section>
  );
}

function DemoStat({
  n,
  label,
  tone,
}: {
  n: number;
  label: string;
  tone: "accent" | "emerald" | "ink";
}) {
  const numColor =
    tone === "accent" ? "text-accent" : tone === "emerald" ? "text-ok" : "text-ink-200";
  return (
    <div>
      <p className={`text-xl font-medium font-mono ${numColor}`}>{n}</p>
      <p className="mt-0.5 text-[10px] uppercase tracking-widest font-mono text-ink-500">
        {label}
      </p>
    </div>
  );
}

function DemoPanel({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="font-mono text-[10px] uppercase tracking-widest text-ink-400 mb-3 pb-2 border-b border-ink-800">
        {label}
      </h3>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function DemoRingsList({ meta }: { meta: TreeMeta }) {
  const rubric = (meta.ring_rubric ?? []) as { ring?: number; name?: string }[];
  const nameByRing = new Map<number, string>();
  for (const r of rubric) {
    if (typeof r.ring === "number" && typeof r.name === "string") {
      nameByRing.set(r.ring, r.name);
    }
  }
  // Active ring for the demo: ring 4 (Features) — highest ring with open
  // questions in the fixture.
  const activeRing = 4;
  return (
    <ul className="space-y-1.5">
      {[0, 1, 2, 3, 4, 5, 6].map((ring) => {
        const name = nameByRing.get(ring) ?? `Ring ${ring}`;
        const isActive = ring === activeRing;
        return (
          <li
            key={ring}
            className={`flex items-baseline gap-2.5 text-xs font-mono ${
              isActive ? "text-ink-100" : "text-ink-500"
            }`}
          >
            <span
              className={`inline-block h-1.5 w-1.5 rounded-full shrink-0 ${
                isActive ? "bg-accent" : "bg-ink-700"
              }`}
            />
            <span className="text-ink-500 w-3 shrink-0">{ring}</span>
            <span className={isActive ? "text-ink-100" : "text-ink-400"}>{name}</span>
          </li>
        );
      })}
    </ul>
  );
}

// Static SVG sketch in lieu of a real graph — the goal is "this is where
// a visualization lives", not to show actual placement. Concentric rings
// with a few node dots so the founder reads it as "graph thumbnail".
function DemoRingsThumbnail() {
  return (
    <svg viewBox="0 0 120 120" className="h-32 w-32 opacity-80">
      <g fill="none" stroke="currentColor" className="text-ink-700">
        {[18, 30, 42, 54].map((r) => (
          <circle key={r} cx="60" cy="60" r={r} strokeWidth="0.5" />
        ))}
      </g>
      <circle cx="60" cy="60" r="3" className="fill-accent" />
      <circle cx="78" cy="60" r="1.5" className="fill-ink-300" />
      <circle cx="42" cy="60" r="1.5" className="fill-ink-300" />
      <circle cx="60" cy="42" r="1.5" className="fill-ink-300" />
      <circle cx="90" cy="60" r="1.5" className="fill-accent" />
      <circle cx="60" cy="102" r="1.5" className="fill-warn" />
      <circle cx="30" cy="78" r="1.5" className="fill-ink-300" />
    </svg>
  );
}

// ---- Step 3: Guided -------------------------------------------------

function GuidedStep({
  onSkip,
  onSubmitStart,
}: {
  onSkip: () => void;
  onSubmitStart: () => void;
}) {
  return (
    <div className="relative w-full max-w-2xl animate-slide-up">
      <div
        className="pointer-events-none absolute -top-32 left-1/2 h-[400px] w-[600px] -translate-x-1/2 rounded-full opacity-15 blur-3xl"
        style={{
          background: "radial-gradient(circle, rgba(124,122,255,0.6), transparent 70%)",
        }}
      />

      <div className="relative">
        <p className="font-mono text-[10px] uppercase tracking-widest text-accent mb-3">
          Your turn
        </p>
        <h2 className="text-display font-medium text-ink-50 tracking-tightest leading-[1.05]">
          Now describe your own idea.
        </h2>
        <p className="mt-4 text-sm text-ink-300 leading-relaxed max-w-xl">
          We'll run the first analysis automatically — about 9 minutes of
          LLM work. You can always run more later.
        </p>

        {/* IdeaForm handles the actual submit flow (POST product, trigger
            run, navigate). We dismiss the overlay flag *just before* the
            navigation completes by hooking a tiny shim around it: the
            user clicking Submit means we're done with onboarding. */}
        <div
          className="mt-10"
          // Capture phase so the click on the inner Begin button flips
          // the flag before the form's mutation kicks off the navigation
          // — works even if the form throws or 402s, the founder still
          // doesn't get the overlay again next mount.
          onClickCapture={(e) => {
            const target = e.target as HTMLElement | null;
            if (target?.closest("button[type='submit']")) {
              onSubmitStart();
            }
          }}
        >
          <IdeaForm variant="page" examples={EXAMPLES} />
        </div>

        <div className="mt-10 flex items-center justify-between">
          <p className="text-[11px] text-ink-500 font-mono leading-relaxed">
            tip · ⌘+↵ to submit · the analysis streams live on the next page
          </p>
          <button
            type="button"
            onClick={onSkip}
            className="text-xs font-mono text-ink-400 hover:text-ink-200 transition-colors"
          >
            skip — I'll figure it out
          </button>
        </div>
      </div>
    </div>
  );
}
